import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["card", "pill", "header", "input", "list"];
  static values = { url: String, x: Number, y: Number, minimized: Boolean };

  connect() {
    this.persistTimer = null;
    this.userExpanded = false;
    this.dragMove = this.dragMove.bind(this);
    this.dragEnd = this.dragEnd.bind(this);

    this.applyMinimized();
  }

  // The card is 300px wide, so on a phone it covers the page and swallows taps
  // meant for what is underneath. Narrow screens show the pill until the reader
  // opens the note themselves.
  get showAsPill() {
    return this.minimizedValue || (window.innerWidth < 768 && !this.userExpanded);
  }

  // A saved position is one the server rendered (the data attributes are
  // non-empty); 0,0 is a real saved position, not "unset".
  hasSavedPosition() {
    const { stickyNoteXValue: x, stickyNoteYValue: y } = this.element.dataset;
    return x !== undefined && x !== "" && y !== undefined && y !== "";
  }

  placeAtSavedPosition() {
    this.element.style.right = "auto";
    this.element.style.bottom = "auto";
    this.element.style.left = `${this.xValue}px`;
    this.element.style.top = `${this.yValue}px`;
    this.clamp();
  }

  // Dock in the bottom-right corner, stacked above the Ask Savant launcher
  // instead of sitting on top of it. Copy the launcher's own offsets rather
  // than deriving them from the viewport: window.innerWidth counts a classic
  // scrollbar, which would leave the note out of line with the button by the
  // width of that scrollbar. The floor keeps the note clear of the mobile
  // bottom nav when the launcher is missing or hidden.
  placeInCorner() {
    const floor = window.innerWidth < 768 ? 88 : 24; // 5.5rem / 1.5rem
    const launcher = document.getElementById("ask-savant-launcher");
    const rect = launcher ? launcher.getBoundingClientRect() : null;
    const style = rect !== null && rect.height > 0 ? window.getComputedStyle(launcher) : null;
    const anchored = style !== null && style.right.endsWith("px") && style.bottom.endsWith("px");

    this.element.style.left = "auto";
    this.element.style.top = "auto";
    this.element.style.right = anchored ? style.right : "1rem";
    this.element.style.bottom = anchored
      ? `${Math.max(parseFloat(style.bottom) + rect.height + 8, floor)}px`
      : `${floor}px`;
  }

  disconnect() {
    window.removeEventListener("pointermove", this.dragMove);
    window.removeEventListener("pointerup", this.dragEnd);
    clearTimeout(this.persistTimer);
  }

  dragStart(event) {
    if (event.target.closest("button")) return;

    const rect = this.element.getBoundingClientRect();
    this.dragState = { startX: event.clientX, startY: event.clientY, left: rect.left, top: rect.top };
    this.element.style.left = `${rect.left}px`;
    this.element.style.top = `${rect.top}px`;
    this.element.style.right = "auto";
    this.element.style.bottom = "auto";
    document.body.style.userSelect = "none";
    window.addEventListener("pointermove", this.dragMove);
    window.addEventListener("pointerup", this.dragEnd);
  }

  dragMove(event) {
    if (!this.dragState) return;

    this.element.style.left = `${this.dragState.left + event.clientX - this.dragState.startX}px`;
    this.element.style.top = `${this.dragState.top + event.clientY - this.dragState.startY}px`;
    this.clamp();
  }

  dragEnd() {
    if (!this.dragState) return;

    this.dragState = null;
    document.body.style.userSelect = "";
    window.removeEventListener("pointermove", this.dragMove);
    window.removeEventListener("pointerup", this.dragEnd);
    this.persist({ position_x: this.currentPosition().x, position_y: this.currentPosition().y });
  }

  minimize() {
    this.minimizedValue = true;
    this.userExpanded = false;
    this.applyMinimized();
    this.persist({ minimized: true });
  }

  expand() {
    this.minimizedValue = false;
    this.userExpanded = true;
    this.applyMinimized();
    this.persist({ minimized: false });
  }

  // Resize only re-lays-out the note when it crosses the phone breakpoint;
  // otherwise it just stays inside the viewport.
  reflow() {
    if (this.showAsPill === this.pilled) this.clamp();
    else this.applyMinimized();
  }

  clamp() {
    const rect = this.element.getBoundingClientRect();
    const left = Math.min(Math.max(rect.left, 0), Math.max(window.innerWidth - rect.width, 0));
    const top = Math.min(Math.max(rect.top, 0), Math.max(window.innerHeight - rect.height, 0));

    if (left !== rect.left || top !== rect.top) {
      this.element.style.left = `${left}px`;
      this.element.style.top = `${top}px`;
      this.element.style.right = "auto";
      this.element.style.bottom = "auto";
    }
  }

  clearInput(event) {
    if (event.detail.success && this.hasInputTarget) {
      this.inputTarget.value = "";
      this.inputTarget.focus();
    }
  }

  // Minimized notes dock as a pill in the bottom-right corner; expanding
  // returns the card to its saved spot (or the corner if it was never moved).
  applyMinimized() {
    this.pilled = this.showAsPill;
    this.pillTarget.classList.toggle("hidden", !this.pilled);
    this.pillTarget.classList.toggle("flex", this.pilled);
    this.cardTarget.classList.toggle("hidden", this.pilled);
    if (this.pilled || !this.hasSavedPosition()) {
      this.placeInCorner();
    } else {
      this.placeAtSavedPosition();
    }
  }

  persist(attrs) {
    if (Object.hasOwn(attrs, "position_x")) { this.xValue = attrs.position_x; this.element.dataset.stickyNoteXValue = String(attrs.position_x); }
    if (Object.hasOwn(attrs, "position_y")) { this.yValue = attrs.position_y; this.element.dataset.stickyNoteYValue = String(attrs.position_y); }
    if (Object.hasOwn(attrs, "minimized")) this.minimizedValue = attrs.minimized;

    clearTimeout(this.persistTimer);
    this.persistTimer = setTimeout(() => {
      fetch(this.urlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken(),
        },
        body: JSON.stringify({ sticky_note: attrs }),
      }).catch(() => console.warn("Could not save sticky note state."));
    }, 300);
  }

  currentPosition() {
    const rect = this.element.getBoundingClientRect();
    return { x: Math.round(rect.left), y: Math.round(rect.top) };
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || "";
  }
}
